import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Button } from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { ArrowRight, MapPin, Briefcase, Users } from "lucide-react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 64px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const JobsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 896px;
  margin: 0 auto;
`;

const JobCard = styled.div`
  padding: 32px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
`;

const JobTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const JobMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const JobDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 16px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const CTASection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const jobs = [
  {
    title: "Senior Frontend Engineer",
    department: "Engineering",
    location: "San Francisco, CA / Remote",
    description: "Build beautiful, performant user interfaces using React and modern web technologies.",
  },
  {
    title: "Product Designer",
    department: "Design",
    location: "New York, NY / Remote",
    description: "Design intuitive experiences that help teams work better together.",
  },
  {
    title: "Customer Success Manager",
    department: "Customer Success",
    location: "Austin, TX / Remote",
    description: "Help our customers succeed and grow with Kliq CRM.",
  },
];

export default function Careers() {
  return (
    <Layout>
      <SEO
        title="Careers"
        description="Join the Kliq CRM team. We're building the future of work and looking for talented people to join us."
        keywords="careers, jobs, hiring, employment"
        hreflang
      />
      <PageTransition>
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Careers</Badge>
              </BadgeWrapper>
              <Title>
                Join our <GradientText>team</GradientText>
              </Title>
              <Description>
                We're building the future of work and looking for talented people to join us on this journey.
              </Description>
            </HeroContent>

            <JobsGrid>
              {jobs.map((job) => (
                <JobCard key={job.title}>
                  <JobHeader>
                    <div>
                      <JobTitle>{job.title}</JobTitle>
                    </div>
                    <JobMeta>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Briefcase size={14} />
                        {job.department}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <MapPin size={14} />
                        {job.location}
                      </div>
                    </JobMeta>
                  </JobHeader>
                  <JobDescription>{job.description}</JobDescription>
                  <Link to="/contact" style={{ textDecoration: "none" }}>
                    <Button variant="outline" size="sm">
                      Apply Now
                      <ArrowRight size={16} />
                    </Button>
                  </Link>
                </JobCard>
              ))}
            </JobsGrid>
          </Container>
        </Section>

        <CTASection>
          <Container>
            <HeroContent>
              <Title style={{ fontSize: "clamp(1.875rem, 3vw, 2.25rem)" }}>Don't see a role that fits?</Title>
              <Description style={{ marginBottom: "32px" }}>
                We're always looking for great people. Send us your resume and we'll keep you in mind for future opportunities.
              </Description>
              <CTAWrapper>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button variant="hero" size="lg">
                    Get in Touch
                    <ArrowRight size={20} />
                  </Button>
                </Link>
              </CTAWrapper>
            </HeroContent>
          </Container>
        </CTASection>
      </PageTransition>
    </Layout>
  );
}
